export interface ReservationEmailData {
  customerName: string;
  podLabel: string;
  dateLabel: string;
  fromLabel: string;
  toLabel: string;
  durationLabel: string;
  amountLabel: string;
  accessCode?: string | null;
}

const GREEN = "#1f3d32";
const CREAM = "#FDFBF7";

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));

export function reservationEmailHtml(d: ReservationEmailData): string {
  d = Object.fromEntries(Object.entries(d).map(([key, value]) => [key, typeof value === "string" ? escapeHtml(value) : value])) as unknown as ReservationEmailData;
  const codeBlock = d.accessCode
    ? `
      <div style="margin:24px 0 0 0;padding:20px;border-radius:12px;background:${CREAM};border:1px solid #e3ded4;text-align:center;">
        <div style="font-size:12px;letter-spacing:0.08em;text-transform:uppercase;color:#6b7280;margin:0 0 8px 0;">Your access code</div>
        <div style="font-family:'Funnel Display',Helvetica,Arial,sans-serif;font-size:32px;font-weight:600;color:${GREEN};letter-spacing:0.12em;">${d.accessCode}</div>
        <div style="font-size:13px;color:#6b7280;margin:10px 0 0 0;">Tap this code into the door handle when you arrive. Please use the pod only during your reserved time.</div>
      </div>`
    : `
      <div style="margin:24px 0 0 0;padding:16px 20px;border-radius:12px;background:${CREAM};border:1px solid #e3ded4;">
        <div style="font-size:14px;color:#4b5563;line-height:1.6;">Your access code will be sent to you before your session starts.</div>
      </div>`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Your qwip reservation</title>
  <link href="https://fonts.googleapis.com/css2?family=Funnel+Display:wght@400;500;600&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet">
</head>
<body style="margin:0;padding:24px 0;background:${CREAM};font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <div style="max-width:560px;margin:0 auto;">
    <div style="text-align:center;padding:0 0 20px 0;">
      <span style="font-family:'Funnel Display',Helvetica,Arial,sans-serif;font-size:26px;font-weight:600;color:${GREEN};">qwip</span>
    </div>

    <div style="background:${CREAM};border:1px solid #e3ded4;border-radius:16px;overflow:hidden;">
      <div style="background:${GREEN};padding:28px 28px;">
        <h1 style="margin:0;font-family:'Funnel Display',Helvetica,Arial,sans-serif;font-size:24px;font-weight:600;color:#ffffff;line-height:1.3;">
          Your pod is reserved
        </h1>
        <p style="margin:8px 0 0 0;font-size:15px;color:rgba(255,255,255,0.85);">${d.podLabel}</p>
      </div>

      <div style="padding:28px;">
        <p style="margin:0 0 20px 0;font-size:16px;color:#374151;line-height:1.6;">
          Hi ${d.customerName}, your reservation is confirmed! Here are the details:
        </p>

        <table style="width:100%;border-collapse:collapse;font-size:15px;color:#374151;">
          <tr>
            <td style="padding:10px 0;border-bottom:1px solid #eeeae2;color:#6b7280;">Date</td>
            <td style="padding:10px 0;border-bottom:1px solid #eeeae2;text-align:right;font-weight:500;">${d.dateLabel}</td>
          </tr>
          <tr>
            <td style="padding:10px 0;border-bottom:1px solid #eeeae2;color:#6b7280;">Time</td>
            <td style="padding:10px 0;border-bottom:1px solid #eeeae2;text-align:right;font-weight:500;">${d.fromLabel} - ${d.toLabel}</td>
          </tr>
          <tr>
            <td style="padding:10px 0;border-bottom:1px solid #eeeae2;color:#6b7280;">Duration</td>
            <td style="padding:10px 0;border-bottom:1px solid #eeeae2;text-align:right;font-weight:500;">${d.durationLabel}</td>
          </tr>
          <tr>
            <td style="padding:10px 0;color:#6b7280;">Paid</td>
            <td style="padding:10px 0;text-align:right;font-weight:500;">${d.amountLabel}</td>
          </tr>
        </table>

        ${codeBlock}

        <h2 style="margin:28px 0 12px;font-family:'Funnel Display',Helvetica,Arial,sans-serif;font-size:20px;color:${GREEN};">📍 How to find the qwip pod</h2>
        <p style="margin:0 0 12px;font-size:15px;line-height:1.7;color:${GREEN};">Head to <strong>The Village</strong> (Westfield London's luxury shopping area, near Louis Vuitton).</p>
        <p style="margin:0 0 12px;font-size:15px;line-height:1.7;color:${GREEN};">Find <strong>Lift Lobby 1</strong> and take the lift or escalators down to Level -3.</p>
        <p style="margin:0 0 12px;font-size:15px;line-height:1.7;color:${GREEN};">You'll find the qwip pod just outside the lifts.</p>
        <p style="margin:0 0 16px;font-size:15px;line-height:1.7;color:${GREEN};">You can also ask any Westfield staff member, or check the Westfield maps to see where the Multi-Faith Prayer Room is. You'll find our pod right next to it!</p>
        <a href="https://maps.app.goo.gl/nb1mJpMrXyXBNwuC7?g_st=ic" style="font-size:15px;color:${GREEN};text-decoration:underline;">Open in Google Maps</a>
        <p style="margin:28px 0 16px;font-size:15px;color:${GREEN};line-height:1.6;">Need help finding us, or have a question about your booking?</p>
        <a href="https://wa.me/447347474059" style="display:inline-block;background:${GREEN};color:#FDFBF7;padding:14px 24px;border-radius:8px;font-size:15px;font-weight:600;text-decoration:none;">Chat on WhatsApp</a>
        <p style="margin:16px 0 0;font-size:13px;color:${GREEN};">You can also reply to this email.</p>
      </div>
    </div>

    <div style="text-align:center;padding:20px 0 0 0;font-size:12px;color:#9ca3af;">
      qwip.co.uk &middot; hello@qwip.co.uk
    </div>
  </div>
</body>
</html>`;
}

